import {
    ElementType,
    Fragment,
    MouseEvent,
    ReactNode,
    useEffect,
    useState,
} from 'react';
import { cn } from '../../utils';
import { useMergedState } from 'rc-util';

export type TabItem = {
    id: string;
    title: React.ReactNode | string;
    href?: string;
    disabled: boolean;
    linkAsProps?: object;
    children?: ReactNode;
};

export type TabsProps = {
    tabs: TabItem[];
    className?: string | object;
    onClick?: (tab: TabItem, event: MouseEvent<HTMLAnchorElement>) => void;
    defaultActive?: string;
    activeKey?: string;
    variant?: 'underline' | 'pill';
    orientation?: 'horizontal' | 'vertical';
    LinkAs?: ElementType;
    childrenAs?: ElementType;
};

export default function Tabs({
    tabs,
    onClick,
    className,
    defaultActive,
    activeKey,
    variant = 'underline',
    orientation = 'horizontal',
    LinkAs,
    childrenAs,
    ...rest
}: TabsProps) {
    const isVertical = orientation === 'vertical';
    const isHorizontal = orientation === 'horizontal';
    const isUnderline = variant === 'underline';
    const [mergedActiveKey, setMergedActiveKey] = useMergedState<string>(
        () => tabs[0]?.id,
        {
            value: activeKey === '' ? undefined : activeKey,
            defaultValue: defaultActive,
        },
    );

    const [activeIndex, setActiveIndex] = useState(() =>
        tabs.findIndex(tab => tab.id === mergedActiveKey),
    );

    useEffect(() => {
        let newActiveIndex = tabs.findIndex(tab => tab.id === mergedActiveKey);
        if (newActiveIndex === -1) {
            newActiveIndex = Math.max(
                0,
                Math.min(activeIndex, tabs.length - 1),
            );
            setMergedActiveKey(tabs[newActiveIndex]?.id);
        }
        setActiveIndex(newActiveIndex);
    }, [tabs.map(tab => tab.id).join('_'), mergedActiveKey, activeIndex]);

    const activeChildren = tabs.find(t => t.id === mergedActiveKey)?.children;
    const LinkAsHolder = LinkAs || DefaultLink;
    const ChildrenAs = childrenAs || Fragment;

    return (
        <div {...rest}>
            <div
                className={cn('alli-block', {
                    [orientationVerticalStyle['direction']]: isVertical,
                })}>
                <div
                    className={cn('alli-border-gray-400', {
                        'alli-border-b': isUnderline && isHorizontal,
                        'alli-max-w-xs alli-flex-shrink-0 alli-border-r':
                            isVertical,
                    })}>
                    <nav
                        className={cn([
                            variants[variant]['parent'],
                            {
                                [orientationVerticalStyle['nav']]: isVertical,
                            },
                            className,
                        ])}
                        aria-label="Tabs">
                        {tabs.map(tab => {
                            return (
                                <LinkAsHolder
                                    key={tab.id}
                                    tab={tab}
                                    setActive={setMergedActiveKey}
                                    onClick={onClick}
                                    className={cn(
                                        {
                                            [variants[variant]['disabled']]:
                                                tab.disabled,
                                            [variants[variant]['active']]:
                                                mergedActiveKey == tab.id,
                                            [variants[variant]['regular']]:
                                                !tab.disabled &&
                                                mergedActiveKey != tab.id,
                                        },
                                        [variants[variant]['common']],
                                    )}>
                                    {tab.title}
                                </LinkAsHolder>
                            );
                        })}
                    </nav>
                </div>
                {!childrenAs && activeChildren && (
                    <div
                        className={cn('alli-w-full', {
                            'alli-mt-4': isHorizontal,
                            'alli-px-4': isVertical,
                        })}>
                        {activeChildren}
                    </div>
                )}
                {childrenAs && activeChildren && (
                    <ChildrenAs>{activeChildren}</ChildrenAs>
                )}
            </div>
        </div>
    );
}

function DefaultLink({
    tab,
    onClick,
    active,
    className,
    setActive,
}: {
    tab: TabItem;
    onClick: (tab: TabItem, event: MouseEvent<HTMLAnchorElement>) => void;
    variant: string;
    active: 'string';
    className?: string;
    setActive: (id: string) => void;
}) {
    return (
        <a
            data-testid={tab.id}
            key={tab.id}
            href={tab.href}
            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                if (tab.disabled) {
                    e.preventDefault();
                    return;
                }
                onClick && onClick(tab, e);
                setActive(tab.id);
            }}
            className={className}
            aria-current={active == tab.id ? 'page' : undefined}>
            {tab.title}
        </a>
    );
}

const variants = {
    underline: {
        disabled:
            'alli-cursor-not-allowed alli-border-transparent alli-text-gray-600',
        active: 'alli-border-blue-600 alli-text-blue-600',
        regular:
            'alli-border-transparent alli-text-gray-750 hover:alli-border-gray-400 hover:alli-text-blue-gray-700 focus-visible:alli-outline-blue-600 alli-cursor-pointer',
        common: 'alli-whitespace-nowrap alli-border-b-2 alli-px-1 alli-py-4 alli-text-sm alli-font-medium',
        parent: '-alli-mb-px alli-flex alli-space-x-8',
        nav: 'alli-flex-col alli-space-x-0 alli-mr-2',
    },
    pill: {
        disabled:
            'alli-cursor-not-allowed alli-border-transparent alli-text-gray-600 focus-visible:alli-outline-none',
        active: 'alli-bg-blue-gray-50 alli-text-blue-700 ',
        regular:
            'alli-text-gray-750 hover:alli-text-blue-gray-700 hover:alli-bg-blue-gray-50 focus-visible:alli-text-blue-600 focus-visible:alli-outline-blue-600 alli-cursor-pointer',
        common: 'alli-rounded-md alli-px-3 alli-py-2 alli-text-sm alli-font-medium',
        parent: 'alli-flex alli-space-x-4',
        nav: 'alli-flex alli-flex-row',
    },
};

const orientationVerticalStyle = {
    nav: 'alli-flex-col alli-space-x-0 alli-mr-2 alli-gap-1',
    direction: 'alli-flex alli-flex-row',
};
